<script setup lang="ts">
import { useEventListener, useMagicKeys } from '@vueuse/core';
import { lockBodyScroll, unlockBodyScroll } from '~/utilities/ui-utilities';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import Button from '~/components/design-system/button.vue';
import type {
  IModalContextBindings,
  IModalProps,
  IModalSizeMap,
} from '~/interfaces/content/modals';

const props = withDefaults(
  defineProps<IModalProps & Partial<IModalContextBindings>>(),
  {
    title: '',
    titleClass: 'font-bold text-base-priority text-lg',
    size: 'md',
    closeOnEsc: true,
    closeOnBackdrop: false,
    showClose: true,
    displayBackground: true,
    displayBorder: false,
    contentStyle: '',
    headerIcon: null,
    headerIconClass: '',
    iconTooltip: '',
    ariaLabel: '',
    appendTo: 'body',
    containerClass: 'h-fit max-h-[95dvh] md:max-h-[90dvh]',
    titleContainerClass: '',
    zIndex: 'z-50',
    type: 'primary',
    forceUntranslatedTitle: false,
  },
);
const emit = defineEmits<{
  (e: 'open'): void;
  (e: 'close'): void;
}>();

const controller = defineModel<boolean>('controller');

const { $t } = useNuxtApp();
const activatorOpen = ref(false);
const isControlled = computed(() => controller.value !== undefined);
const open = computed({
  get: () => (isControlled.value ? !!controller.value : activatorOpen.value),
  set: (v: boolean) => {
    if (isControlled.value) controller.value = v;
    else activatorOpen.value = v;
  },
});

const { escape } = useMagicKeys();
watch(escape, (isDown) => {
  if (isDown && props.closeOnEsc && open.value) close();
});

const show = () => {
  if (!open.value) {
    open.value = true;
    emit('open');
  }
};
const close = () => {
  if (open.value) {
    open.value = false;
    emit('close');
  }
};

watch(open, (v) => (v ? lockBodyScroll(true) : unlockBodyScroll()), {
  immediate: true,
});

const sizeMap: IModalSizeMap = {
  sm: '20.5rem',
  md: '26.25rem',
  lg: '48rem',
  xl: '64rem',
};
const dialogWidth = computed(() =>
  ['sm', 'md', 'lg', 'xl'].includes(String(props.size))
    ? sizeMap[props.size as keyof IModalSizeMap]
    : props.size,
);
const bodyBg = computed(() => {
  if (props.type === 'toast') return 'bg-transparent';
  return props.type === 'secondary'
    ? 'bg-base-secondary'
    : 'bg-layer-secondary';
});
const titleClassMap: Record<string, string> = {
  account: 'px-4 bg-layer rounded-t-2xl hidden md:block',
  primary: 'bg-base p-3',
  secondary: 'bg-layer',
};
const mergeTitleClass = computed(
  () =>
    `${titleClassMap[props.type] ?? 'px-3 rounded-t-lg bg-base'} ${props.titleContainerClass}`,
);
const mergeBodyClass = computed(() => {
  const border = props.displayBorder
    ? 'border border-gray-200 dark:border-gray-600'
    : 'border-none';
  return `${border} ${bodyBg.value}`;
});

const footerClass = computed(() =>
  props?.type === 'secondary' ? 'bg-layer' : 'bg-base',
);

onUnmounted(() => unlockBodyScroll());
onMounted(() => useEventListener('popstate', close));
</script>

<template>
  <div>
    <div v-if="$slots?.['modal-activator']" @click="show">
      <slot name="modal-activator" />
    </div>
    <Teleport :to="appendTo">
      <div
        v-if="displayBackground && open"
        class="fixed inset-0 bg-dark-900/50 backdrop-blur-sm"
        :class="[zIndex]"
      />
      <Transition name="modal">
        <div v-if="open" class="fixed inset-0" :class="[zIndex]">
          <div
            class="absolute inset-0 flex items-start justify-center overflow-hidden container !p-2 md:!p-4"
            @click="closeOnBackdrop && close()"
          >
            <div
              class="md:mt-12 w-full max-w-full rounded-lg shadow-2xl relative overflow-y-auto overflow-x-hidden"
              :class="[bodyBg, containerClass]"
              :style="[
                {
                  width: dialogWidth,
                  maxWidth: 'calc(100vw - 0.5rem)',
                },
                contentStyle,
              ]"
              role="dialog"
              aria-modal="true"
              :aria-labelledby="title ? `${title}-modal-title` : undefined"
              :aria-label="!title ? ariaLabel : undefined"
              @click.stop
              @touch.stop
            >
              <div
                v-if="title"
                class="sticky top-0 left-0 z-10 flex items-center py-3"
                :class="[mergeTitleClass]"
              >
                <div
                  v-if="props.headerIcon"
                  class="flex flex-row mx-2 items-center"
                >
                  <component
                    :is="props.headerIcon"
                    :class="props.headerIconClass"
                    :tooltip="$t(props.iconTooltip)"
                  />
                </div>
                <div class="flex justify-between w-full items-center">
                  <span
                    :id="`${title}-modal-title`"
                    :class="['capitalize text-ellipsis truncate', titleClass]"
                    v-html="title"
                  />
                  <Button
                    v-if="props.showClose"
                    element-name="close-modal"
                    :type="
                      type === 'secondary' || type === 'account'
                        ? 'tertiary-alternative'
                        : 'tertiary'
                    "
                    padding="sm"
                    rounding="md"
                    aria-label="Close"
                    @click="close"
                  >
                    <XMarkIcon />
                  </Button>
                </div>
              </div>
              <div
                class="h-full relative box-content"
                :class="[mergeBodyClass]"
              >
                <slot name="modal-content" />
              </div>
              <div
                v-if="$slots['footer-content']"
                :class="footerClass"
                class="p-2 md:p-4"
              >
                <slot name="footer-content" />
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  @apply transition-all ease-in-out duration-[0.2s];
}
.modal-enter-from,
.modal-leave-to {
  @apply scale-[0.8] opacity-0;
}
</style>
